import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { partnerDiscountService, prospectService } from '../services/api';
import { PartnerCampaignResponse, PartnerEligibilitySummary } from '../types';
import { useAuth } from '../contexts/AuthContext';

const PartnerDiscount: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user, login } = useAuth();
  const [campaign, setCampaign] = useState<PartnerCampaignResponse | null>(null);
  const [eligibility, setEligibility] = useState<PartnerEligibilitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const [leadForm, setLeadForm] = useState({ first_name: '', last_name: '', email: '' });
  const [emailInput, setEmailInput] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [memberId, setMemberId] = useState('');
  const [lastName, setLastName] = useState('');

  useEffect(() => {
    if (!slug) return;
    const load = async () => {
      try {
        setLoading(true);
        const data = await partnerDiscountService.getCampaign(slug);
        setCampaign(data);
        setError('');
      } catch (err: any) {
        setError(err.response?.data?.error || 'Unable to load partner campaign.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  useEffect(() => {
    if (!isAuthenticated) return;
    partnerDiscountService.getEligibility()
      .then(setEligibility)
      .catch(() => undefined);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !user?.email) return;
    setEmailInput(user.email);
  }, [isAuthenticated, user?.email]);

  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const timer = window.setInterval(() => {
      setCooldownSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownSeconds]);

  const isEligibleForCampaign = useMemo(() => {
    if (!eligibility || !campaign) return false;
    return eligibility.grants.some((grant) => grant.campaign_id === campaign.campaign.id && grant.is_active);
  }, [eligibility, campaign]);

  const handleLeadSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setMessage('');
    try {
      const response = await prospectService.createProspect(leadForm);
      if (response.temporary_password) {
        const success = await login(leadForm.email, response.temporary_password);
        if (success) {
          setMessage('Account created. Continue to verify your eligibility.');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to create account.');
    }
  };

  const handleSendOtp = async () => {
    if (!slug) return;
    setSendingCode(true);
    setError('');
    setMessage('');
    try {
      await partnerDiscountService.sendEmailOtp(slug, emailInput);
      setMessage('Verification code sent. Check your email.');
      setCooldownSeconds(120);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to send verification code.');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerify = async () => {
    if (!slug || !campaign) return;
    setVerifying(true);
    setError('');
    setMessage('');
    try {
      if (campaign.org.verification_type === 'email_otp') {
        await partnerDiscountService.verifyEmail(slug, emailInput, otpCode);
      } else {
        await partnerDiscountService.verifyMember(slug, memberId, lastName, emailInput || undefined);
      }
      setMessage('Eligibility verified! Your discount is attached to your account.');
      const summary = await partnerDiscountService.getEligibility();
      setEligibility(summary);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Verification failed.');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-200">
        {error || 'Partner campaign not found.'}
      </div>
    );
  }

  const discountLabel = campaign.campaign.discount_type === 'percent'
    ? `${campaign.campaign.discount_value}%`
    : `${campaign.campaign.currency} ${campaign.campaign.discount_value.toFixed(2)}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-emerald-50 to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="rounded-3xl border border-emerald-100/60 bg-white/90 dark:bg-slate-900/80 dark:border-slate-700 shadow-xl p-8">
          <p className="text-xs uppercase tracking-[0.3em] text-emerald-500">Partner Discount</p>
          <h1 className="text-3xl font-semibold text-slate-900 dark:text-white mt-3">{campaign.org.name} Student Offer</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Verify your eligibility once. Your discount will follow your account automatically.
          </p>

          <div className="mt-6 rounded-2xl border border-emerald-200/70 bg-emerald-50/80 dark:bg-emerald-900/20 dark:border-emerald-800/60 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <p className="text-xs uppercase tracking-widest text-emerald-600 dark:text-emerald-300">Discount</p>
                <p className="text-lg font-semibold text-emerald-700 dark:text-emerald-200">{discountLabel} off</p>
              </div>
              <span className="text-xs text-emerald-700/80 dark:text-emerald-100/80">
                Applied automatically after verification
              </span>
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
          {message && (
            <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {message}
            </div>
          )}

          {!isAuthenticated && (
            <form onSubmit={handleLeadSubmit} className="mt-8 space-y-4">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Create your student access</h2>
              <p className="text-sm text-slate-600 dark:text-slate-300">We’ll create your account so the discount can be tied to you.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  required
                  placeholder="First name"
                  value={leadForm.first_name}
                  onChange={(event) => setLeadForm((prev) => ({ ...prev, first_name: event.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
                />
                <input
                  type="text"
                  required
                  placeholder="Last name"
                  value={leadForm.last_name}
                  onChange={(event) => setLeadForm((prev) => ({ ...prev, last_name: event.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
                />
              </div>
              <input
                type="email"
                required
                placeholder="Email address"
                value={leadForm.email}
                onChange={(event) => setLeadForm((prev) => ({ ...prev, email: event.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
              />
              <button
                type="submit"
                className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                Create account and continue
              </button>
            </form>
          )}

          {isAuthenticated && (
            <div className="mt-8 space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Verify eligibility</h2>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  {campaign.org.verification_type === 'email_otp'
                    ? 'Use your organization email to receive a one-time code.'
                    : campaign.org.verification_type === 'member_id'
                      ? 'Enter your organization ID to confirm eligibility.'
                      : 'We will validate your details with your organization.'}
                </p>
              </div>

              {campaign.org.verification_type === 'email_otp' ? (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(event) => setEmailInput(event.target.value)}
                      placeholder="Organization email"
                      className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
                    />
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={sendingCode || !emailInput || cooldownSeconds > 0}
                      className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed dark:bg-white dark:text-slate-900"
                    >
                      {sendingCode ? 'Sending...' : cooldownSeconds > 0 ? `Resend in ${cooldownSeconds}s` : 'Send code'}
                    </button>
                  </div>
                  {cooldownSeconds > 0 && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      You can request a new code after the timer ends.
                    </p>
                  )}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="text"
                      value={otpCode}
                      onChange={(event) => setOtpCode(event.target.value)}
                      placeholder="Enter 6-digit code"
                      className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
                    />
                    <button
                      type="button"
                      onClick={handleVerify}
                      disabled={verifying || !otpCode}
                      className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {verifying ? 'Verifying...' : 'Verify'}
                    </button>
                  </div>
                </div>
              ) : campaign.org.verification_type === 'member_id' ? (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={memberId}
                    onChange={(event) => setMemberId(event.target.value)}
                    placeholder="Organization ID"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
                  />
                  <input
                    type="text"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    placeholder="Last name"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
                  />
                  <button
                    type="button"
                    onClick={handleVerify}
                    disabled={verifying || !memberId || !lastName}
                    className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {verifying ? 'Verifying...' : 'Verify eligibility'}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(event) => setEmailInput(event.target.value)}
                    placeholder="Organization email (optional)"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
                  />
                  <input
                    type="text"
                    value={memberId}
                    onChange={(event) => setMemberId(event.target.value)}
                    placeholder="Organization ID"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
                  />
                  <input
                    type="text"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    placeholder="Last name"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700"
                  />
                  <button
                    type="button"
                    onClick={handleVerify}
                    disabled={verifying || !memberId || !lastName}
                    className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {verifying ? 'Verifying...' : 'Verify eligibility'}
                  </button>
                </div>
              )}

              {isEligibleForCampaign && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  Discount verified and attached to your account.
                </div>
              )}

              <div className="pt-4">
                <button
                  type="button"
                  onClick={() => navigate('/student-pathway')}
                  className="rounded-full bg-emerald-600 px-6 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Continue to your pipeline
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerDiscount;
